/**
 * Embolsado, lo puro (docs/esquema.md §5.7): de quién depende cada mutación en la cola
 * (regla 20). El `padre_id` es la MUTACIÓN local de la que depende —nunca el registro—;
 * con más de una candidata gana la de la rama pendiente más larga y, a igual largo, la más
 * nueva por `seq`: es la que tendrá acuse última, y el orden de la cola garantiza las demás.
 * La proyección de los pallets abiertos (regla 21) llega con el catálogo `pallets_abiertos`.
 */

import { BorradorIncompleto } from './produccion';
import { fechaLocal, instanteLocal } from './tiempo';

/** Una fila pendiente de la cola, con lo que hace falta para elegir un padre. */
export interface FilaPendiente {
	id: string;
	epoch: string;
	seq: number;
	tipo: string;
	payload: unknown;
	/** De quién depende, si sigue pendiente: por acá se mide el largo de su rama. */
	padre_id?: string;
}

function campo(payload: unknown, clave: string): string | null {
	if (typeof payload !== 'object' || payload === null) return null;
	const valor = (payload as Record<string, unknown>)[clave];
	return typeof valor === 'string' ? valor : null;
}

function numero(payload: unknown, clave: string): number | null {
	if (typeof payload !== 'object' || payload === null) return null;
	const valor = (payload as Record<string, unknown>)[clave];
	return typeof valor === 'number' ? valor : null;
}

/** Cuántas pendientes tiene por delante en su propia rama (padre, abuelo…): tantos viajes como eso espera antes de salir. */
/** Un objeto anidado del payload (`parte` de una unión de a partes, §12.21), o null. */
function objeto(payload: unknown, clave: string): unknown {
	if (typeof payload !== 'object' || payload === null) return null;
	const valor = (payload as Record<string, unknown>)[clave];
	return typeof valor === 'object' && valor !== null ? valor : null;
}

function largoDeLaRama(f: FilaPendiente, pendientes: FilaPendiente[]): number {
	let largo = 0;
	const vistos = new Set<string>();
	for (let padre = f.padre_id; padre !== undefined && !vistos.has(padre); ) {
		vistos.add(padre);
		const fila = pendientes.find((x) => x.id === padre);
		if (!fila) break;
		largo += 1;
		padre = fila.padre_id;
	}
	return largo;
}

/**
 * La que tendrá acuse última: la de la rama pendiente más larga y, a igual largo, la más nueva por
 * (epoch, seq) —un epoch más nuevo (ULID mayor) va después de todo lo del anterior—. Solo por `seq`
 * no alcanzaba: la unión de una tarima cuya apertura y carga siguen pendientes sale dos viajes
 * después que una carga más nueva sin padre, y el cierre que colgaba de esa carga viajaba antes que
 * la unión y caía en `composicion_cambio` (Codex, ronda de la v5 entera, 15/9).
 */
function masNueva(filas: FilaPendiente[], pendientes: FilaPendiente[] = filas): FilaPendiente | undefined {
	let mejor: { fila: FilaPendiente; largo: number } | undefined;
	for (const f of filas) {
		const largo = largoDeLaRama(f, pendientes);
		if (mejor === undefined || largo > mejor.largo || (largo === mejor.largo && (f.epoch > mejor.fila.epoch || (f.epoch === mejor.fila.epoch && f.seq > mejor.fila.seq)))) {
			mejor = { fila: f, largo };
		}
	}
	return mejor?.fila;
}

/** De varios padres posibles (ids de pendientes), el que tendrá acuse último: el único `padre_id` que una mutación lleva (r. 20). */
export function masNuevaDe(pendientes: FilaPendiente[], ids: Array<string | undefined>): string | undefined {
	return masNueva(pendientes.filter((f) => ids.includes(f.id)), pendientes)?.id;
}

const esApertura = (f: FilaPendiente, palletId: string) => f.tipo === 'pallet.abrir' && campo(f.payload, 'pallet_id') === palletId;

/** `envasado.crear`: la apertura local del pallet, si sigue pendiente. */
export function padreDeCarga(pendientes: FilaPendiente[], palletId: string): string | undefined {
	return pendientes.find((f) => esApertura(f, palletId))?.id;
}

/**
 * A qué pallet pertenece una carga, siguiendo la cadena de correcciones DENTRO de la cola; una
 * raíz que no está en la cola (vino del catálogo) la resuelve el llamador.
 */
function palletDeLaCarga(pendientes: FilaPendiente[], envasadoId: string, resolver: (id: string) => string | null, vistos = new Set<string>()): string | null {
	if (vistos.has(envasadoId)) return null;
	vistos.add(envasadoId);
	const fila = pendientes.find((f) => (f.tipo === 'envasado.crear' || f.tipo === 'envasado.corregir' || f.tipo === 'carga.corregir') && campo(f.payload, 'envasado_id') === envasadoId);
	if (!fila) return resolver(envasadoId);
	if (fila.tipo === 'envasado.crear') return campo(fila.payload, 'pallet_id');
	const anterior = campo(fila.payload, 'corrige_a');
	return anterior === null ? null : palletDeLaCarga(pendientes, anterior, resolver, vistos);
}

/** ¿Esta pendiente toca el pallet? Su apertura y sus transiciones, una unión que lo tiene de destino, y las cargas que van a él. */
function tocaAlPallet(f: FilaPendiente, palletId: string, pendientes: FilaPendiente[], resolver: (id: string) => string | null): boolean {
	if (f.tipo.startsWith('pallet.')) {
		// Una unión toca la tarima, el destino y, de a partes (§12.21), la parte que nace unida.
		return (
			campo(f.payload, 'pallet_id') === palletId ||
			(f.tipo === 'pallet.unir' && (campo(f.payload, 'destino_pallet_id') === palletId || campo(objeto(f.payload, 'parte'), 'pallet_id') === palletId))
		);
	}
	if (f.tipo === 'envasado.crear') return campo(f.payload, 'pallet_id') === palletId;
	// §12.18: `carga.corregir` y `carga.anular` tocan el pallet igual que las de antes (y un cierre pendiente espera a una corrección).
	if (f.tipo === 'envasado.corregir' || f.tipo === 'carga.corregir') {
		const anterior = campo(f.payload, 'corrige_a');
		return anterior !== null && palletDeLaCarga(pendientes, anterior, resolver) === palletId;
	}
	if (f.tipo === 'envasado.anular' || f.tipo === 'carga.anular') {
		const target = campo(f.payload, 'envasado_id');
		return target !== null && palletDeLaCarga(pendientes, target, resolver) === palletId;
	}
	return false;
}

export interface OpcionesDeTransicion {
	/** En unir: el destino, cuya apertura local pendiente también cuenta (la más nueva de las dos gana). */
	destinoId?: string;
	/** A qué pallet pertenece una carga que no está en la cola (vino del catálogo). */
	palletDeEnvasado?: (envasadoId: string) => string | null;
}

/**
 * `pallet.cerrar`, `pallet.unir`, `pallet.anular`, `pallet.corregir`: la última mutación local
 * pendiente que toque ese pallet —su apertura, una carga, una corrección o anulación de una carga,
 * una unión— y, en unir, la apertura del destino si es local y sigue pendiente.
 */
export function padreDeTransicion(pendientes: FilaPendiente[], palletId: string, opciones: OpcionesDeTransicion = {}): string | undefined {
	const resolver = opciones.palletDeEnvasado ?? (() => null);
	const candidatas = pendientes.filter(
		(f) => tocaAlPallet(f, palletId, pendientes, resolver) || (opciones.destinoId !== undefined && esApertura(f, opciones.destinoId)),
	);
	return masNueva(candidatas, pendientes)?.id;
}

/**
 * `pallet.abrir` de una letra y formato: el cierre o la anulación local pendiente de la misma
 * combinación (si no, `pallet_abierto_repetido` contra un cierre que todavía no viajó). La letra
 * y el formato del pallet que se cierra salen de su apertura local, o del catálogo si vino de ahí.
 */
export function padreDeApertura(
	pendientes: FilaPendiente[],
	letra: string,
	formatoBolsaG: number,
	letraFormatoDe: (palletId: string) => { letra: string | null; formato_bolsa_g: number } | null,
): string | undefined {
	const candidatas = pendientes.filter((f) => {
		if (f.tipo !== 'pallet.cerrar' && f.tipo !== 'pallet.anular') return false;
		const palletId = campo(f.payload, 'pallet_id');
		if (palletId === null) return false;
		const apertura = pendientes.find((a) => esApertura(a, palletId));
		const datos = apertura ? { letra: campo(apertura.payload, 'letra'), formato_bolsa_g: numero(apertura.payload, 'formato_bolsa_g') } : letraFormatoDe(palletId);
		return datos !== null && datos.letra === letra && datos.formato_bolsa_g === formatoBolsaG;
	});
	return masNueva(candidatas, pendientes)?.id;
}

/**
 * `pallet.abrir` de una tarima de espera: la anulación o la unión local pendiente de OTRA tarima
 * del mismo precinto (si no, la carga nueva viajaría antes y caería en `tarima_repetida`: los
 * hijos esperan el acuse de su padre, y las dos aperturas salían juntas; Codex, ronda de la
 * segunda tanda, 15/9). El precinto de una tarima que solo existe en la cola sale de su carga
 * pendiente; el de una del catálogo, del resolvedor.
 */
export function padreDeTarima(pendientes: FilaPendiente[], llenadoId: string, llenadoDeTarima: (palletId: string) => string | null): string | undefined {
	const precintoDe = (palletId: string): string | null => {
		const carga = pendientes.find((f) => f.tipo === 'envasado.crear' && campo(f.payload, 'pallet_id') === palletId);
		return carga ? campo(carga.payload, 'llenado_id') : llenadoDeTarima(palletId);
	};
	const candidatas = pendientes.filter((f) => {
		if (f.tipo !== 'pallet.anular' && f.tipo !== 'pallet.unir') return false;
		const palletId = campo(f.payload, 'pallet_id');
		return palletId !== null && precintoDe(palletId) === llenadoId;
	});
	return masNueva(candidatas, pendientes)?.id;
}

// ── Proyección de los pallets abiertos (§5.7 r. 21) ─────────────────────────

/** Una carga vigente tal como viaja en `pallets_abiertos` (§5.5): por identidad, con su raíz. */
export interface EnvasadoServidor {
	envasado_id: string;
	raiz_id: string;
	llenado_id: string;
	/** El precinto del silo; null si el silo se anuló (ya no tiene versión vigente) o la carga es local y el catálogo no lo trae. */
	precinto: string | null;
	cant_bolsas: number;
	registrado_en: string;
	persona_id: string;
	/** La letra de la muestra vigente del precinto HOY (Q/P/L/descarte), o null si no tiene. */
	letra_de_la_muestra: string | null;
	/** El estado vigente del silo (cerrado, descartado, abierto), o null si se anuló. */
	estado_del_silo: string | null;
	/** Cargada en este teléfono y todavía sin acuse (la proyección la marca): el saldo del precinto la descuenta igual. */
	local?: boolean;
	/**
	 * Cuándo llegó la carga al servidor (el `sincronizado_en` de su raíz, ISO UTC): el ancla de las 48 horas para corregirla
	 * desde el teléfono (§12.18). Un catálogo de antes no lo trae, y una carga local todavía no llegó: las dos se ofrecen.
	 */
	llego?: string;
	/** La mutación pendiente que creó esta versión de la carga (la proyección la marca): una corrección cuelga de ella (§12.18). */
	mutacion_id?: string;
}

/** Una tarima ya unida a un pallet abierto: sin número, se nombra por su hora y quiénes la armaron (§5.5). */
export interface TarimaUnidaServidor {
	pallet_id: string;
	abierto_en: string;
	unido_en: string;
	/** §12.21: nació unida al repartir una tarima de espera (una parte): sus bolsas no se corrigen ni se anulan por separado. Un catálogo de antes no lo trae. */
	es_parte?: 0 | 1;
	personas: string[];
	envasados: EnvasadoServidor[];
}

/** Un pallet o una tarima vigente en `abierto` o `espera`, como lo manda el servidor (§5.5). */
export interface PalletAbiertoServidor {
	pallet_id: string;
	version_id: string;
	/** El número del rótulo que se le puso al abrir (§5.7 r. 22); null en una tarima. */
	numero: number | null;
	es_tarima: 0 | 1;
	letra: string | null;
	formato_bolsa_g: number;
	abierto_en: string;
	/** §12.21: una tarima en espera de la que ya salió una parte a un pallet: no se tira ni se le corrige la carga hasta deshacer el pase. Un catálogo de antes no lo trae. */
	tiene_partes?: 0 | 1;
	personas: string[];
	envasados: EnvasadoServidor[];
	tarimas: TarimaUnidaServidor[];
}

/**
 * Un pallet cerrado de las últimas 48 horas (§12.18): lo de `pallets_abiertos` más el estado (`consumido` si entró a una
 * mezcla), el cierre, el testigo y si salió con remito. Esos dos casos se ven, pero los corrige la oficina.
 */
export interface PalletCerradoServidor extends PalletAbiertoServidor {
	estado: 'cerrado' | 'consumido';
	cerrado_en: string;
	bolsas: number;
	despachado: 0 | 1;
}

/** `pallets_cerrados_recientes` (§12.18, solo a envasado): `desde` es la hora del servidor menos 48 horas, en ISO UTC. */
export interface PalletsCerradosRecientes {
	desde: string;
	pallets: PalletCerradoServidor[];
}

/**
 * Una mutación a proyectar: una fila pendiente de la cola, o un recibo (con su raíz). El número del
 * pallet viaja en el payload de la apertura (§5.7 r. 22); `numero` es lo que el acuse devolvió, que
 * es el mismo, y sigue leyéndose por si un recibo viejo no lo trae en el payload.
 */
export interface MutacionProyectable {
	tipo: string;
	payload: unknown;
	mutacion_id?: string;
	raiz_id?: string;
	numero?: number;
}

export interface PalletProyectado extends PalletAbiertoServidor {
	/** Abierto en este teléfono y todavía sin acuse: existe para quien embolsa, no para el servidor. */
	local: boolean;
	/** La mutación que lo abrió, mientras siga en la cola: de ella dependen sus cargas. */
	abierto_mutacion_id: string | null;
	/** Bolsas reales: las suyas más las de sus tarimas unidas (§4.2, «lo derivado»). */
	bolsas: number;
	/** «Calidad cambió» (r. 19): una carga cuya muestra ya no acompaña la letra, o cuyo silo se descartó o anuló después. El D acepta cualquier letra. */
	calidad_cambio: boolean;
	/** En una tarima: la letra de la muestra de su precinto, si ya existe; es lo que habilita «pasar a pallet». */
	letra_de_la_muestra: string | null;
}

export interface ResolvedoresDeProyeccion {
	/** La letra de la muestra vigente de un precinto (de `llenados_para_envasar`), para las cargas que el catálogo no trae. */
	letraDe?: (llenadoId: string) => string | null;
	/** El precinto de un silo, para una carga que el catálogo no trae. */
	precintoDe?: (llenadoId: string) => string | null;
}

function lista(payload: unknown, clave: string): string[] {
	if (typeof payload !== 'object' || payload === null) return [];
	const valor = (payload as Record<string, unknown>)[clave];
	return Array.isArray(valor) ? valor.filter((x): x is string => typeof x === 'string') : [];
}

function copiaDe(p: PalletAbiertoServidor): PalletProyectado {
	return {
		...p,
		envasados: p.envasados.map((e) => ({ ...e })),
		tarimas: p.tarimas.map((t) => ({ ...t, envasados: t.envasados.map((e) => ({ ...e })) })),
		local: false,
		abierto_mutacion_id: null,
		bolsas: 0,
		calidad_cambio: false,
		letra_de_la_muestra: null,
	};
}

/** Los campos que una corrección o un recibo fundido pueden traer para una carga ya conocida. */
function actualizarCarga(e: EnvasadoServidor, payload: unknown): void {
	e.envasado_id = campo(payload, 'envasado_id') ?? e.envasado_id;
	e.cant_bolsas = numero(payload, 'cant_bolsas') ?? e.cant_bolsas;
	e.registrado_en = campo(payload, 'registrado_en') ?? e.registrado_en;
	e.persona_id = campo(payload, 'persona_id') ?? e.persona_id;
}

/** Lo que este teléfono sabe de un pallet que pasó por acá: letra, formato y número; lo sigue sabiendo después de cerrarlo. */
export interface PalletConocido {
	letra: string | null;
	formato_bolsa_g: number;
	/** El número del rótulo (r. 22): del catálogo, del acuse, o del payload de una apertura todavía pendiente. */
	numero: number | null;
	/** La campaña del pallet, si se sabe (el payload de la apertura la trae; el catálogo solo trae los de la abierta). */
	campania_id: string | null;
}

/**
 * Una carga tal como la proyección la deja, por raíz, cuando difiere de lo que el catálogo cuenta:
 * una creación que el catálogo no refleja —pendiente, o con acuse más nuevo que el snapshot—, o
 * una carga reflejada que este teléfono corrigió o anuló y el catálogo todavía cuenta como estaba.
 */
export interface CargaNoReflejada {
	llenado_id: string;
	cant_bolsas: number;
	formato_bolsa_g: number;
	/**
	 * Las bolsas que el catálogo ya cuenta de esta raíz (0 si la creación no se reflejó): el saldo
	 * del precinto descuenta solo la diferencia, también negativa. Sin esto, corregir 8 → 9 acá
	 * dejaba pasar una bolsa que el servidor rechazaba (Codex, ronda del diff de la tolerancia, 15/9).
	 */
	reflejadas: number;
}

/** Lo no reflejado de un precinto: los gramos que descontar del saldo del catálogo, y el formato con que ya se embolsó. */
export interface SaldoNoReflejado {
	gramos: number;
	formato_bolsa_g: number | null;
}

/** Un pallet cerrado (o consumido por una mezcla) de las últimas 48 horas, con la cola encima (§12.18). */
export interface PalletCerradoProyectado extends PalletProyectado {
	estado: 'cerrado' | 'consumido';
	cerrado_en: string;
	despachado: 0 | 1;
}

export interface ProyeccionDeEmbolsado {
	/** Los pallets en armado y las tarimas en espera, como los ve quien embolsa. */
	pallets: PalletProyectado[];
	/**
	 * §12.18: los cerrados de las últimas 48 horas —los del catálogo y los que este teléfono cerró y el catálogo todavía
	 * no trae—, el más nuevo primero, con sus cargas corregidas o anuladas acá encima.
	 */
	cerrados: PalletCerradoProyectado[];
	/** Las cargas en las que la proyección difiere del catálogo, aunque su pallet ya se haya cerrado desde acá: de ellas sale el saldo de cada precinto. */
	cargas: Map<string, CargaNoReflejada>;
	/** Todo pallet del que se sabe algo, por `pallet_id`, cerrado o no: para el padre de una apertura y para nombrarlo. */
	palletsConocidos: Map<string, PalletConocido>;
}

/**
 * Lo que quien embolsa tiene delante = lo que el servidor sabe + lo que este teléfono hizo y
 * todavía no subió, aplicado POR IDENTIDAD (§5.7 r. 21): una creación entra solo si su raíz no
 * está; una corrección reemplaza a su raíz; una anulación la quita; una apertura ya reflejada
 * no se duplica. Así un acuse perdido con la creación ya en el catálogo no duplica bolsas. Las
 * mutaciones vienen en orden de aplicación (confirmadas sin reflejar primero, después la cola).
 * Una creación PENDIENTE cuya identidad el catálogo ya trae no pisa nada: el servidor la aplicó
 * (se perdió el acuse) y lo que el catálogo cuenta es tan nuevo o más —gestión pudo corregir entre
 * medio, y volver a la versión del payload mandaba el cierre siguiente con una base vieja—; un
 * recibo, en cambio, es más nuevo que el snapshot por construcción y sí manda: fundido con una
 * corrección, además anota la diferencia para el saldo del precinto (Codex, ronda de la v5
 * entera, 15/9).
 *
 * Además de los pallets abiertos devuelve lo que la pantalla necesita saber de lo que YA NO está
 * abierto (Codex, ronda del diff del paso 7, 14/9): las cargas no reflejadas —el saldo y el
 * formato de un precinto no pueden volver atrás porque su pallet se cerró acá, ni porque el
 * acuse llegó antes que el catálogo— y la letra, el formato y el número de cada pallet que pasó
 * por la cola —una apertura tiene que esperar al cierre pendiente de su letra y formato aunque
 * el pallet que se cierra solo exista en un recibo—.
 */
export function proyeccionDeEmbolsado(
	delServidor: PalletAbiertoServidor[],
	mutaciones: MutacionProyectable[],
	resolvedores: ResolvedoresDeProyeccion = {},
	cerradosDelServidor: PalletCerradoServidor[] = [],
): ProyeccionDeEmbolsado {
	const letraDe = resolvedores.letraDe ?? (() => null);
	const precintoDe = resolvedores.precintoDe ?? (() => null);
	const abiertos = new Map<string, PalletProyectado>();
	const cerrados = new Map<string, PalletCerradoProyectado>();
	const unidas = new Map<string, TarimaUnidaServidor>();
	const palletsConocidos = new Map<string, PalletConocido>();
	const cargas = new Map<string, CargaNoReflejada>();
	for (const p of delServidor) {
		const copia = copiaDe(p);
		abiertos.set(p.pallet_id, copia);
		palletsConocidos.set(p.pallet_id, { letra: p.letra, formato_bolsa_g: p.formato_bolsa_g, numero: p.numero, campania_id: null });
		for (const t of copia.tarimas) unidas.set(t.pallet_id, t);
	}
	// §12.18: los cerrados de las últimas 48 horas también se proyectan por identidad: una corrección o una anulación
	// de una de sus cargas los cambia acá, y el saldo del precinto la cuenta igual que en un pallet abierto.
	for (const p of cerradosDelServidor) {
		if (abiertos.has(p.pallet_id)) continue;
		const copia: PalletCerradoProyectado = { ...copiaDe(p), estado: p.estado, cerrado_en: p.cerrado_en, despachado: p.despachado };
		cerrados.set(p.pallet_id, copia);
		if (!palletsConocidos.has(p.pallet_id)) palletsConocidos.set(p.pallet_id, { letra: p.letra, formato_bolsa_g: p.formato_bolsa_g, numero: p.numero, campania_id: null });
		for (const t of copia.tarimas) unidas.set(t.pallet_id, t);
	}
	/** Dónde viven las cargas de un pallet: en el abierto, o en una tarima ya unida. */
	const envasadosDe = (palletId: string) => abiertos.get(palletId)?.envasados ?? cerrados.get(palletId)?.envasados ?? unidas.get(palletId)?.envasados;
	/** Cada lista de cargas con el formato de su pallet; la tarima unida embolsa con el del destino (`pallet_de_otro_formato`). */
	const todasLasListas = () => [
		...[...abiertos.values(), ...cerrados.values()].flatMap((p) => [{ lista: p.envasados, formato: p.formato_bolsa_g }, ...p.tarimas.map((t) => ({ lista: t.envasados, formato: p.formato_bolsa_g }))]),
		...[...unidas.values()].map((t) => ({ lista: t.envasados, formato: palletsConocidos.get(t.pallet_id)?.formato_bolsa_g ?? 0 })),
	];
	const buscar = (objetivo: string): { lista: EnvasadoServidor[]; indice: number; formato: number } | null => {
		for (const { lista, formato } of todasLasListas()) {
			const indice = lista.findIndex((x) => x.envasado_id === objetivo || x.raiz_id === objetivo);
			if (indice >= 0) return { lista, indice, formato };
		}
		return null;
	};
	/** La raíz de cada versión de carga que pasó por acá: una segunda corrección de la cadena apunta a la primera, no a la raíz. */
	const raizDe = new Map<string, string>();
	/**
	 * Lo que el catálogo cuenta de una carga reflejada que este teléfono corrige o anula, anotado la
	 * primera vez que se la toca: de ahí sale la diferencia que el saldo descuenta o devuelve.
	 */
	const anotarReflejada = (raiz: string, carga: EnvasadoServidor, formato: number) => {
		if (!cargas.has(raiz)) cargas.set(raiz, { llenado_id: carga.llenado_id, cant_bolsas: carga.cant_bolsas, formato_bolsa_g: formato, reflejadas: carga.cant_bolsas });
	};
	/**
	 * El número que cuenta una mutación (r. 22): mientras la apertura es local (pendiente), el del
	 * payload; con acuse, el que devolvió el servidor —que manda aunque el payload diga otro: gestión
	 * pudo renumerar el pallet entre la apertura y el acuse (Codex, ronda del diff de 7b, 15/9)—.
	 */
	const numeroDe = (m: MutacionProyectable) => (m.mutacion_id !== undefined ? numeroDeApertura(m.payload) : (m.numero ?? numeroDeApertura(m.payload)));
	/** Lo que una mutación cuenta del pallet: el número y, al abrir, letra, formato y campaña. */
	const conocer = (palletId: string, m: MutacionProyectable, apertura: unknown) => {
		const previo = palletsConocidos.get(palletId);
		palletsConocidos.set(palletId, {
			letra: campo(apertura, 'letra') ?? previo?.letra ?? null,
			formato_bolsa_g: numero(apertura, 'formato_bolsa_g') ?? previo?.formato_bolsa_g ?? 0,
			numero: (apertura === null ? m.numero : numeroDe(m)) ?? previo?.numero ?? null,
			campania_id: campo(apertura, 'campania_id') ?? previo?.campania_id ?? null,
		});
	};

	// Primero, qué se sabe de cada pallet que pasó por acá: letra, formato y campaña viajan en la
	// apertura y el recibo de una transición los conserva debajo (`reciboDeEmbolsado`). Va antes de
	// aplicar nada porque el recibo de una carga puede llegar ANTES que el del cierre de su pallet
	// —cada acuse lo fecha— y la carga necesita el formato para descontar del precinto (Codex,
	// ronda de la v5 entera, 15/9: se descontaban cero gramos).
	for (const m of mutaciones) {
		const palletId = campo(m.payload, 'pallet_id');
		if (palletId === null || !m.tipo.startsWith('pallet.') || palletsConocidos.has(palletId)) continue;
		const formato = numero(m.payload, 'formato_bolsa_g');
		if (formato === null) continue;
		palletsConocidos.set(palletId, { letra: campo(m.payload, 'letra'), formato_bolsa_g: formato, numero: null, campania_id: campo(m.payload, 'campania_id') });
	}

	for (const m of mutaciones) {
		const pl = m.payload;
		const palletId = campo(pl, 'pallet_id');
		const pendiente = m.mutacion_id !== undefined;
		if (palletId !== null && m.tipo.startsWith('pallet.')) conocer(palletId, m, m.tipo === 'pallet.abrir' ? pl : null);
		switch (m.tipo) {
			case 'pallet.abrir': {
				if (palletId === null) break;
				const existente = abiertos.get(palletId);
				if (existente) {
					// Ya reflejado: una apertura pendiente no pisa al catálogo (el servidor ya la aplicó y lo
					// de allá es tan nuevo o más); un recibo —fundido con una corrección, o más nuevo que
					// el snapshot— sí: los datos, encima; sin duplicar.
					if (pendiente) break;
					existente.abierto_en = campo(pl, 'abierto_en') ?? existente.abierto_en;
					existente.version_id = campo(pl, 'version_id') ?? existente.version_id;
					const personas = lista(pl, 'personas');
					if (personas.length > 0) existente.personas = personas;
					// Un pallet que el catálogo ya refleja conserva el número del catálogo (que es más nuevo
					// que cualquier acuse proyectado); solo un acuse lo pisa, nunca el payload viejo.
					existente.numero = m.numero ?? existente.numero;
					break;
				}
				abiertos.set(palletId, {
					pallet_id: palletId,
					version_id: campo(pl, 'version_id') ?? '',
					// El número viene en el payload desde que el rótulo se elige al abrir (r. 22); con acuse, el del servidor.
					numero: numeroDe(m),
					es_tarima: numero(pl, 'es_tarima') === 1 ? 1 : 0,
					letra: campo(pl, 'letra'),
					formato_bolsa_g: numero(pl, 'formato_bolsa_g') ?? 0,
					abierto_en: campo(pl, 'abierto_en') ?? '',
					personas: lista(pl, 'personas'),
					envasados: [],
					tarimas: [],
					local: m.mutacion_id !== undefined,
					abierto_mutacion_id: m.mutacion_id ?? null,
					bolsas: 0,
					calidad_cambio: false,
					letra_de_la_muestra: null,
				});
				break;
			}
			case 'pallet.corregir': {
				const p = palletId === null ? undefined : abiertos.get(palletId);
				if (!p) break;
				p.abierto_en = campo(pl, 'abierto_en') ?? p.abierto_en;
				p.version_id = campo(pl, 'version_id') ?? p.version_id;
				const personas = lista(pl, 'personas');
				if (personas.length > 0) p.personas = personas;
				break;
			}
			case 'pallet.cerrar': {
				if (palletId === null) break;
				const p = abiertos.get(palletId);
				abiertos.delete(palletId);
				// §12.18: el pallet que se cerró acá sigue a la vista entre los cerrados, con sus cargas corregibles, hasta que
				// el catálogo lo traiga (si ya lo trae, manda el del catálogo).
				if (p && !cerrados.has(palletId)) {
					cerrados.set(palletId, { ...p, version_id: campo(pl, 'version_id') ?? p.version_id, estado: 'cerrado', cerrado_en: campo(pl, 'cerrado_en') ?? '', despachado: 0, local: pendiente || p.local });
				}
				break;
			}
			case 'pallet.anular':
				if (palletId !== null) {
					abiertos.delete(palletId);
					cerrados.delete(palletId);
				}
				break;
			case 'pallet.unir': {
				if (palletId === null) break;
				const tarima = abiertos.get(palletId);
				const destino = campo(pl, 'destino_pallet_id');
				const dest = destino === null ? undefined : abiertos.get(destino);
				const bolsas = numero(pl, 'bolsas');
				const parte = objeto(pl, 'parte');
				const parteId = campo(parte, 'pallet_id');
				const parteEnvasadoId = campo(parte, 'envasado_id');
				if (bolsas === null || parteId === null || parteEnvasadoId === null) {
					// Entera: la tarima sale de los abiertos y cuelga del destino con sus cargas.
					abiertos.delete(palletId);
					if (tarima && dest && !dest.tarimas.some((t) => t.pallet_id === palletId)) {
						const unida: TarimaUnidaServidor = { pallet_id: palletId, abierto_en: tarima.abierto_en, unido_en: campo(pl, 'cerrado_en') ?? '', personas: tarima.personas, envasados: tarima.envasados };
						dest.tarimas.push(unida);
						unidas.set(palletId, unida);
					}
					break;
				}
				// De a partes (§12.21): la original sigue en espera con el resto y la versión nueva (la base del pase
				// siguiente); la parte cuelga del destino con una carga copiada de la original —misma llegada—. Si la
				// original no está en los abiertos, el catálogo ya la refleja unida entera más tarde: no se inventa nada.
				// El saldo del precinto no cambia: estas cargas no entran en `cargas`.
				if (!tarima) break;
				const cargaOriginal = tarima.envasados[0];
				if (cargaOriginal === undefined) break;
				const versionNueva = campo(pl, 'version_id');
				const cargaVersion = campo(pl, 'carga_version_id');
				// Idempotente: si el catálogo ya trae la versión nueva de la carga, el pase ya está reflejado.
				if (cargaVersion !== null && cargaOriginal.envasado_id !== cargaVersion) {
					cargaOriginal.envasado_id = cargaVersion;
					cargaOriginal.cant_bolsas = Math.max(0, (numero(pl, 'bolsas_vistas') ?? cargaOriginal.cant_bolsas) - bolsas);
					raizDe.set(cargaVersion, cargaOriginal.raiz_id);
					if (pendiente) cargaOriginal.mutacion_id = m.mutacion_id;
					if (versionNueva !== null) tarima.version_id = versionNueva;
				}
				palletsConocidos.set(parteId, { letra: null, formato_bolsa_g: tarima.formato_bolsa_g, numero: null, campania_id: palletsConocidos.get(palletId)?.campania_id ?? null });
				if (dest && !dest.tarimas.some((t) => t.pallet_id === parteId)) {
					const unida: TarimaUnidaServidor = {
						pallet_id: parteId,
						abierto_en: tarima.abierto_en,
						unido_en: campo(pl, 'cerrado_en') ?? '',
						es_parte: 1,
						personas: tarima.personas,
						envasados: [
							{
								envasado_id: parteEnvasadoId,
								raiz_id: parteEnvasadoId,
								llenado_id: cargaOriginal.llenado_id,
								precinto: cargaOriginal.precinto,
								cant_bolsas: bolsas,
								registrado_en: cargaOriginal.registrado_en,
								persona_id: cargaOriginal.persona_id,
								letra_de_la_muestra: cargaOriginal.letra_de_la_muestra ?? letraDe(cargaOriginal.llenado_id),
								estado_del_silo: cargaOriginal.estado_del_silo,
								...(cargaOriginal.llego === undefined ? {} : { llego: cargaOriginal.llego }),
								...(pendiente ? { local: true, mutacion_id: m.mutacion_id } : {}),
							},
						],
					};
					dest.tarimas.push(unida);
					unidas.set(parteId, unida);
				}
				break;
			}
			case 'envasado.crear': {
				const raiz = m.raiz_id ?? campo(pl, 'envasado_id');
				const llenadoId = campo(pl, 'llenado_id');
				if (raiz === null || llenadoId === null || palletId === null) break;
				const yaEsta = buscar(raiz);
				if (yaEsta) {
					// Una creación pendiente cuya raíz el catálogo ya trae no pisa nada (acuse perdido: lo del
					// catálogo es tan nuevo o más). Un recibo fundido con una corrección sí, y anota lo que el
					// catálogo contaba para que el saldo del precinto descuente la diferencia.
					if (pendiente) break;
					const carga = yaEsta.lista[yaEsta.indice];
					const bolsas = numero(pl, 'cant_bolsas');
					if (bolsas !== null && bolsas !== carga.cant_bolsas) {
						anotarReflejada(raiz, carga, yaEsta.formato);
						const conocida = cargas.get(raiz);
						if (conocida) conocida.cant_bolsas = bolsas;
					}
					actualizarCarga(carga, pl);
					break;
				}
				const bolsas = numero(pl, 'cant_bolsas') ?? 0;
				// Se anota aunque el pallet ya no esté abierto acá: el saldo del precinto la descuenta igual.
				cargas.set(raiz, { llenado_id: llenadoId, cant_bolsas: bolsas, formato_bolsa_g: palletsConocidos.get(palletId)?.formato_bolsa_g ?? 0, reflejadas: 0 });
				raizDe.set(campo(pl, 'envasado_id') ?? raiz, raiz);
				envasadosDe(palletId)?.push({
					envasado_id: campo(pl, 'envasado_id') ?? raiz,
					raiz_id: raiz,
					llenado_id: llenadoId,
					precinto: precintoDe(llenadoId),
					cant_bolsas: bolsas,
					registrado_en: campo(pl, 'registrado_en') ?? '',
					persona_id: campo(pl, 'persona_id') ?? '',
					letra_de_la_muestra: letraDe(llenadoId),
					estado_del_silo: 'cerrado',
					...(pendiente ? { local: true, mutacion_id: m.mutacion_id } : {}),
				});
				break;
			}
			case 'envasado.corregir':
			case 'carga.corregir': {
				const objetivo = m.raiz_id ?? campo(pl, 'corrige_a');
				if (objetivo === null) break;
				const hallada = buscar(objetivo);
				const raiz = m.raiz_id ?? raizDe.get(objetivo) ?? hallada?.lista[hallada.indice].raiz_id ?? objetivo;
				if (hallada) {
					anotarReflejada(raiz, hallada.lista[hallada.indice], hallada.formato);
					// §12.18: `carga.corregir` solo cambia las bolsas; su `persona_id` es quien corrige, no quien cargó.
					actualizarCarga(hallada.lista[hallada.indice], m.tipo === 'carga.corregir' ? { envasado_id: campo(pl, 'envasado_id'), cant_bolsas: numero(pl, 'cant_bolsas') } : pl);
					if (pendiente) hallada.lista[hallada.indice].mutacion_id = m.mutacion_id;
				}
				const conocida = cargas.get(raiz);
				if (conocida) conocida.cant_bolsas = numero(pl, 'cant_bolsas') ?? conocida.cant_bolsas;
				const version = campo(pl, 'envasado_id');
				if (version !== null) raizDe.set(version, raiz);
				break;
			}
			case 'envasado.anular':
			case 'carga.anular': {
				const objetivo = m.raiz_id ?? campo(pl, 'envasado_id');
				if (objetivo === null) break;
				const hallada = buscar(objetivo);
				const raiz = m.raiz_id ?? raizDe.get(objetivo) ?? hallada?.lista[hallada.indice].raiz_id ?? objetivo;
				if (hallada) {
					anotarReflejada(raiz, hallada.lista[hallada.indice], hallada.formato);
					hallada.lista.splice(hallada.indice, 1);
				}
				// Anular una carga que el catálogo cuenta devuelve su saldo (lo que NO lo devuelve es un
				// pallet anulado por metales, §4.2: sus cargas siguen vigentes); una local se olvida.
				const conocida = cargas.get(raiz);
				if (conocida?.reflejadas === 0) cargas.delete(raiz);
				else if (conocida) conocida.cant_bolsas = 0;
				break;
			}
			default:
				break;
		}
	}

	for (const p of cerrados.values()) p.bolsas = [...p.envasados, ...p.tarimas.flatMap((t) => t.envasados)].reduce((suma, e) => suma + e.cant_bolsas, 0);
	// §12.21: los precintos con una parte vigente (una tarima nacida unida, también pendiente acá): su tarima en espera tiene partes.
	const precintosConPartes = new Set([...unidas.values()].filter((t) => t.es_parte === 1).flatMap((t) => t.envasados.map((e) => e.llenado_id)));
	for (const p of abiertos.values()) {
		const cargasDelPallet = [...p.envasados, ...p.tarimas.flatMap((t) => t.envasados)];
		p.bolsas = cargasDelPallet.reduce((suma, e) => suma + e.cant_bolsas, 0);
		p.calidad_cambio = p.es_tarima === 0 && cargasDelPallet.some((e) => e.estado_del_silo !== 'cerrado' || e.letra_de_la_muestra === null || (p.letra !== 'D' && e.letra_de_la_muestra !== p.letra));
		const primera = p.es_tarima === 1 ? p.envasados[0] : undefined;
		p.letra_de_la_muestra = primera ? (primera.letra_de_la_muestra ?? letraDe(primera.llenado_id)) : null;
		if (p.es_tarima === 1) p.tiene_partes = p.tiene_partes === 1 || (primera !== undefined && precintosConPartes.has(primera.llenado_id)) ? 1 : 0;
	}
	return {
		pallets: [...abiertos.values()].sort((a, b) => a.abierto_en.localeCompare(b.abierto_en) || a.pallet_id.localeCompare(b.pallet_id)),
		cerrados: [...cerrados.values()].sort((a, b) => b.cerrado_en.localeCompare(a.cerrado_en) || a.pallet_id.localeCompare(b.pallet_id)),
		cargas,
		palletsConocidos,
	};
}

/** Solo los pallets abiertos de la proyección. */
export function proyectarPallets(delServidor: PalletAbiertoServidor[], mutaciones: MutacionProyectable[], resolvedores: ResolvedoresDeProyeccion = {}): PalletProyectado[] {
	return proyeccionDeEmbolsado(delServidor, mutaciones, resolvedores).pallets;
}

/**
 * Lo que la proyección difiere del catálogo, sumado por precinto: los gramos que descontar del
 * saldo del catálogo (negativos si acá se corrigió para abajo o se anuló una carga que el catálogo
 * cuenta), y el formato con que ya se embolsó.
 */
export function saldosNoReflejados(cargas: Map<string, CargaNoReflejada>): Map<string, SaldoNoReflejado> {
	const porLlenado = new Map<string, SaldoNoReflejado>();
	for (const c of cargas.values()) {
		const saldo = porLlenado.get(c.llenado_id) ?? { gramos: 0, formato_bolsa_g: null };
		saldo.gramos += (c.cant_bolsas - c.reflejadas) * c.formato_bolsa_g;
		if (saldo.formato_bolsa_g === null && c.formato_bolsa_g > 0 && c.cant_bolsas > 0) saldo.formato_bolsa_g = c.formato_bolsa_g;
		porLlenado.set(c.llenado_id, saldo);
	}
	return porLlenado;
}

// ── Los borradores de la estación (§5.7; boceto `../Perejil/public/poc/embolsado.html`) ──

export type LetraPallet = 'Q' | 'P' | 'L' | 'D';
export const LETRAS_PALLET: readonly LetraPallet[] = ['Q', 'P', 'L', 'D'];
export const NOMBRE_LETRA_PALLET: Record<LetraPallet, string> = { Q: 'Q · apta Virginia', P: 'P', L: 'L', D: 'D · descarte' };
/** 8,25 primero: es lo ideal; 7,25 cuando la mercadería no entra (Agata, 14/9). */
export const FORMATOS_BOLSA = [8250, 7250] as const;
export const FORMATO_PREMARCADO = 8250;
/** El estándar (Agata, 14/9): al llegar la app propone cerrar; con menos o con más, avisa. */
export const BOLSAS_ESTANDAR = 36;
export const MAX_BOLSAS_POR_CARGA = 99;
/** Hasta cuatro embolsan un pallet (Agata, 5/9 y 14/9). */
export const MAX_PERSONAS_PALLET = 4;
/** La alerta pedida el 5/9: un precinto con letra que lleva cinco días sin embolsar. */
export const DIAS_DE_ALERTA = 5;

interface ComunEmbolsado {
	capture_id: string;
	mutacion_id: string;
	paso: number;
	/** Quien carga ESTE registro: la dueña del teléfono; el muleto pregunta. */
	personas: string[];
	actualizado_en: number;
}

/** El precinto elegido, congelado al elegirlo: lo que la pantalla muestra y lo que el payload lleva. */
export interface PrecintoVisto {
	llenado_id: string;
	precinto: string;
	silo_numero: string;
	lote: string;
	/** Q, P, L o D; null en un precinto sin muestra, que solo va a una tarima de espera (§5.7 r. 18 i). */
	letra: string | null;
	/** La campaña del silo: la apertura de una tarima la lleva (no tiene rótulo que la traiga); los borradores viejos no la tienen. */
	campania_id?: string;
	/** Null en un silo sin pesada: no hay saldo que mostrar ni tope que aplicar (el servidor tampoco decide). */
	neto_g: number | null;
	/** Lo ya envasado de este precinto (servidor + lo que este teléfono cambió y el catálogo no refleja), en gramos. */
	envasado_g: number;
	formato_previo: number | null;
	/** v6 (§12.5): la versión vigente de la muestra, base de «Cambiar la calidad». Los borradores viejos no la tienen. */
	muestra_id?: string;
	/** v6 (§12.9): la muestra no tiene humedad todavía: la letra es provisoria. */
	falta_humedad?: boolean;
	/**
	 * §12.21: el precinto tiene bolsas en una tarima de espera a la que ya le llegó la letra («una tarima es un precinto»,
	 * Agata, 27/10/2026): las primeras bolsas que se carguen salen de ahí, como una unión de a partes. Null si no tiene.
	 */
	en_tarima?: TarimaDelPrecinto | null;
}

/** La tarima de espera del precinto, tal como se vio al elegirlo: la base del CAS, sus bolsas (el testigo) y su única carga. */
export interface TarimaDelPrecinto {
	pallet_id: string;
	version_id: string;
	abierto_en: string;
	personas: string[];
	formato_bolsa_g: number;
	/** Las bolsas vigentes de la tarima: `bolsas_vistas` del payload. */
	bolsas: number;
	/** La única carga de la tarima: de ella heredan la parte y el resto. */
	envasado_id: string;
	registrado_en: string;
	persona_id: string;
	/** La hora de la última carga: el pase no puede quedar antes (`cierre_antes_de_la_carga`). */
	ultima_carga_en: string | null;
	/** La mutación pendiente que la abrió, si sigue en la cola. */
	padre_mutacion_id: string | null;
}

/** El pallet destino tal como se vio: la base del CAS, el número si ya lo tiene y lo que hay que esperar. */
export interface PalletVisto {
	pallet_id: string;
	version_id: string;
	numero: number | null;
	/** La letra de la campaña abierta cuando se eligió: la partida del rótulo no cambia si la campaña cambia después. */
	campania_letra: string | null;
	letra: string | null;
	formato_bolsa_g: number;
	bolsas: number;
	precintos: number;
	abierto_en: string;
	/** La hora de la última carga directa, si hay: el cierre no puede quedar antes. */
	ultima_carga_en: string | null;
	/** La del primer envasado (directo o de una tarima unida): de ahí sale el vencimiento del rótulo. */
	primera_carga_en: string | null;
	personas: string[];
	/** La mutación que lo abrió si sigue en la cola: de ella dependen sus cargas y su cierre. */
	padre_mutacion_id: string | null;
}

/**
 * El rótulo que se le pone al pallet nuevo (§5.7 r. 22), persistido como el par campaña y número:
 * la campaña es la del `pallet.abrir`, así un catálogo que cambió de campaña con el borrador a
 * medias no lo mueve de campaña. `del_catalogo` dice si el número figuraba como emitido cuando se
 * eligió; uno tipeado que no figura viaja igual (el servidor valida) y la revisión lo avisa.
 */
export interface RotuloElegido {
	campania_id: string;
	numero: number;
	del_catalogo: boolean;
}

export interface BorradorEmbolsar extends ComunEmbolsado {
	tipo: 'embolsar';
	/** Se entró tocando la tarjeta del precinto en el inicio: el paso de elegirlo se saltea. */
	desde_inicio: boolean;
	/**
	 * La tarima de espera (§5.7 r. 18 i; boceto «Tarima de espera»): un precinto SIN letra, solo,
	 * porque faltó la persona de calidad. Nace acá con su primera carga —`nuevo` lleva su identidad,
	 * sin rótulo— y no pregunta pallet, rótulo ni quiénes: la arma quien carga. Los borradores viejos
	 * no tienen el campo.
	 */
	tarima?: boolean;
	envasado_id: string;
	precinto: PrecintoVisto | null;
	formato_bolsa_g: number | null;
	bolsas: number | null;
	/** El pallet abierto elegido, o null si va a uno nuevo. */
	destino: PalletVisto | null;
	/** «Pallet nuevo»: nace acá con identidad propia y con el rótulo que se le pone; su apertura viaja delante de la carga. `rotulo_propuesto`: ya se propuso uno (si la embolsadora lo borró, no se vuelve a proponer). */
	nuevo: { pallet_id: string; version_id: string; mutacion_id: string; rotulo: RotuloElegido | null; rotulo_propuesto?: boolean } | null;
	/** Quiénes embolsan el pallet nuevo (hasta cuatro). */
	personas_pallet: string[];
	registrado_ms: number | null;
	nota: string | null;
	/**
	 * §12.21: con el precinto en una tarima de espera, la identidad del pase: la versión nueva de la tarima (la unión), la
	 * versión nueva de su carga, la parte que nace unida y la mutación de la unión cuando además hay bolsas nuevas. Nace al
	 * elegir el precinto y se conserva entre pantallas: confirmar dos veces no crea dos. Null sin tarima; los borradores
	 * viejos no lo tienen.
	 */
	reparto?: Reparto | null;
}

/** Los ids de una unión de a partes (§12.21), generados en el teléfono. */
export interface Reparto {
	version_id: string;
	carga_version_id: string;
	mutacion_id: string;
	parte: { pallet_id: string; version_id: string; envasado_id: string };
}

export interface BorradorCerrarPallet extends ComunEmbolsado {
	tipo: 'cerrar_pallet';
	/** Se entró tocando la tarjeta del pallet en el inicio: el paso de elegirlo se saltea. */
	desde_inicio: boolean;
	pallet: PalletVisto | null;
	version_id: string;
	cerrado_ms: number | null;
}

/**
 * Por qué una tarima todavía no pasa a un pallet (r. 17d), dicho para la tarjeta; null si puede.
 * Un silo descartado después de embolsar (aceite, contaminación grave en su muestra) no entra a un
 * pallet aunque la muestra diga «descarte»: el servidor lo rechaza con `precinto_descartado`
 * (Codex, ronda de la segunda tanda, 15/9).
 */
export function porQueNoPasaAPallet(t: { bolsas: number; letra_de_la_muestra: string | null; estado_del_silo: string | null }): string | null {
	if (t.bolsas === 0) return 'sin bolsas todavía';
	if (t.estado_del_silo === null) return 'el silo se anuló: la oficina decide';
	if (t.estado_del_silo === 'descartado') return 'el silo se descartó: no pasa a un pallet';
	if (t.estado_del_silo !== 'cerrado') return 'el silo se reabrió: espera a que vuelva a cerrarse';
	if (t.letra_de_la_muestra === null) return 'esperando la letra de calidad';
	return null;
}

/** «Metales» es la revisión de los imanes al final del turno; «otro», lo demás (se rompió, se mojó…). */
export const MOTIVOS_ANULACION_PALLET = [
	['metales', 'la revisión de los imanes'],
	['otro', 'se rompió, se mojó…'],
] as const;
export type MotivoAnulacionPallet = (typeof MOTIVOS_ANULACION_PALLET)[number][0];

/**
 * Se tira un pallet (§5.7 r. 17c; boceto «Se tira un pallet»): anulación con motivo de un pallet
 * en armado o de una tarima en espera. Sus bolsas no vuelven al silo (§4.2): la merma se conoce.
 * No tiene vuelta desde el teléfono; si fue un error, lo reabre la oficina.
 */
export interface BorradorAnularPallet extends ComunEmbolsado {
	tipo: 'anular_pallet';
	/** Se entró tocando el botón con el pallet ya elegido. */
	desde_inicio: boolean;
	pallet: PalletVisto | null;
	version_id: string;
	motivo: MotivoAnulacionPallet | null;
	cerrado_ms: number | null;
}

/** Una mutación caída que se vuelve a encolar con identidad nueva: lo que hace falta de la fila del outbox. */
export interface MutacionCaida {
	id: string;
	tipo: string;
	payload: unknown;
	entidad: 'pallet' | 'envasado';
	registro_id: string;
	personas: string[];
	padre_id: string | null;
	/** El `Resumen` de la fila (lo tipa `app/datos/bd.ts`, que este módulo puro no importa). */
	resumen: unknown;
}

/**
 * Rehacer con otro rótulo (§5.7 r. 22): una apertura que el servidor rechazó por el rótulo
 * —`rotulo_no_emitido`, `rotulo_anulado`, `rotulo_usado`, letra o formato— con bolsas ya cargadas
 * detrás. Se pregunta el rótulo correcto y vuelven a la cola la apertura y todo lo que dependía de
 * ella (cargas, cierre) como mutaciones NUEVAS: ids nuevos, el `pallet_id` y las versiones se
 * conservan porque nunca entraron, y los padres se reconstruyen. Como calidad con una muestra
 * rechazada por la regla.
 */
export interface BorradorRehacerPallet extends ComunEmbolsado {
	tipo: 'rehacer_pallet';
	/** La apertura caída, tal como estaba en la bandeja. */
	apertura: MutacionCaida;
	/** Lo que colgaba de ella, en el orden de la cola (hijos, nietos…). */
	hijos: MutacionCaida[];
	/** Por qué cayó, para decirlo en la pantalla. */
	motivo: string;
	rotulo: RotuloElegido | null;
	rotulo_propuesto?: boolean;
}

/**
 * v6 (§12.5): envasado decide la letra final del precinto. La versión de la muestra que se vio es la
 * base del CAS; la nueva nace acá. Motivo obligatorio.
 */
export interface BorradorCambiarLetra extends ComunEmbolsado {
	tipo: 'cambiar_letra';
	muestra_id: string;
	muestra_base_id: string | null;
	llenado_id: string | null;
	precinto: string | null;
	silo_numero: string | null;
	/** La letra de la muestra hoy (`descarte`, no `D`): la que se cambia. */
	letra_actual: 'Q' | 'P' | 'L' | 'descarte' | null;
	letra: 'Q' | 'P' | 'L' | 'descarte' | null;
	motivo: string | null;
}

/** v6 (§12.4): una nota al precinto desde envasado. */
export interface BorradorNotaPrecinto extends ComunEmbolsado {
	tipo: 'nota_precinto';
	nota_id: string;
	llenado_id: string | null;
	precinto: string | null;
	texto: string | null;
}

/**
 * §12.18: una carga tal como se vio al elegirla para corregirla o anularla. Su versión es la base del CAS; el pallet es
 * el efectivo —el destino, si la carga vino de una tarima unida—, con su estado y sus bolsas, para decir antes de mandar
 * cómo queda y por qué no se puede.
 */
export interface CargaVista {
	envasado_id: string;
	raiz_id: string;
	llenado_id: string;
	precinto: string | null;
	cant_bolsas: number;
	/** Quien la cargó: no cambia al corregirla. */
	persona_id: string;
	registrado_en: string;
	formato_bolsa_g: number;
	/** El pallet efectivo: el destino, si la carga vino de una tarima unida. */
	pallet_id: string;
	/** El pallet donde está la carga: la tarima, si vino de una. */
	pallet_directo_id: string;
	pallet_numero: number | null;
	pallet_estado: 'abierto' | 'espera' | 'cerrado' | 'consumido';
	pallet_bolsas: number;
	/** Cuántas cargas tiene el pallet efectivo: la única de un cerrado no se anula (`ultima_carga_del_pallet`). */
	pallet_cargas: number;
	despachado: boolean;
	/** Cuándo llegó la carga al servidor (ISO), o null si todavía no llegó o el catálogo no lo dice. */
	llego: string | null;
	/** La mutación pendiente que creó esta versión de la carga, fijada al elegirla (como §12.17, h. 6). */
	padre_mutacion_id: string | null;
	/** §12.21: es una de las dos cargas de un pase (la de la parte, o la de la tarima original mientras tenga una parte vigente): no se corrige ni se anula por separado. */
	de_un_pase?: boolean;
}

/** §12.18: corregir las bolsas de una carga, también con el pallet cerrado. */
export interface BorradorCorregirCarga extends ComunEmbolsado {
	tipo: 'corregir_carga';
	carga: CargaVista | null;
	/** La versión nueva de la carga. */
	envasado_id: string;
	/** La versión nueva del pallet, si el servidor tiene que rehacer el testigo del cierre. */
	pallet_version_id: string;
	bolsas: number | null;
}

/** Por qué se anula una carga: lo que más pasa, y «otro» con el motivo en una línea. */
export const MOTIVOS_ANULACION_CARGA = [
	['se anotó dos veces', 'la misma carga, cargada otra vez'],
	['era de otro precinto', 'se anota de nuevo en el precinto que era'],
	['otro', 'contalo en una línea'],
] as const;

/** §12.18: anular una carga, con motivo. */
export interface BorradorAnularCarga extends ComunEmbolsado {
	tipo: 'anular_carga';
	carga: CargaVista | null;
	pallet_version_id: string;
	motivo: (typeof MOTIVOS_ANULACION_CARGA)[number][0] | null;
	/** Con «otro»: el motivo, en una línea. */
	motivo_otro: string | null;
}

/** El pallet nace con su primera carga (`BorradorEmbolsar.nuevo`): no hay un borrador de apertura aparte (2/10). La tarima que pasa a un pallet también va por `embolsar` desde el 27/10 (§12.21: `en_tarima`). */
export type BorradorEmbolsado =
	| BorradorEmbolsar
	| BorradorCerrarPallet
	| BorradorRehacerPallet
	| BorradorAnularPallet
	| BorradorCambiarLetra
	| BorradorNotaPrecinto
	| BorradorCorregirCarga
	| BorradorAnularCarga;

/**
 * Por qué una carga no se corrige desde el teléfono (§12.18), dicho para la planta, o null si se puede. `desde` es el
 * borde del snapshot (la hora del servidor menos 48 horas, ISO), o null si no se conoce: entonces decide el servidor. Una
 * carga que todavía no llegó (`llego` null) siempre se ofrece.
 */
export function porQueNoSeCorrige(c: Pick<CargaVista, 'pallet_estado' | 'despachado' | 'llego' | 'de_un_pase'>, desde: string | null): string | null {
	if (c.de_un_pase) return 'vino de un reparto: la oficina lo deshace y se reparte de nuevo';
	if (c.despachado) return 'salió con remito: la corrige la oficina';
	if (c.pallet_estado === 'consumido') return 'entró a una mezcla: la corrige la oficina';
	if (c.llego !== null && desde !== null && c.llego < desde) return 'se cargó hace más de 48 horas: la corrige la oficina';
	return null;
}

/** La única carga de un pallet cerrado no se anula: el testigo es de al menos una bolsa, y el pallet entero lo anula la oficina. */
export function esLaUnicaDeUnCerrado(c: Pick<CargaVista, 'pallet_estado' | 'pallet_cargas'>): boolean {
	return c.pallet_estado === 'cerrado' && c.pallet_cargas <= 1;
}

/**
 * El tope del precinto al corregir (§12.18): lo envasado sin esta carga más la carga nueva, y solo si sube —bajar nunca
 * empeora—. La misma regla que `superaLaTolerancia` y que el servidor.
 */
export function superaLaToleranciaAlCorregir(p: Pick<PrecintoVisto, 'neto_g' | 'envasado_g'>, actual: number, nueva: number, formatoG: number): boolean {
	if (nueva <= actual) return false;
	return superaLaTolerancia({ neto_g: p.neto_g, envasado_g: p.envasado_g - gramosDeCarga(actual, formatoG) }, nueva, formatoG);
}

/** El motivo de anular una carga como viaja: el del chip, o el que se escribió con «otro». */
export function motivoDeAnularCarga(b: Pick<BorradorAnularCarga, 'motivo' | 'motivo_otro'>): string | null {
	if (b.motivo === null) return null;
	if (b.motivo !== 'otro') return b.motivo;
	const texto = (b.motivo_otro ?? '').trim();
	return texto === '' ? null : texto;
}

export function armarPayloadCargaCorregir(b: BorradorCorregirCarga): Record<string, unknown> {
	const carga = exigir(b.carga, 'la carga');
	return { envasado_id: b.envasado_id, corrige_a: carga.envasado_id, cant_bolsas: exigir(b.bolsas, 'las bolsas'), persona_id: exigir(b.personas[0], 'quién corrige'), pallet_version_id: b.pallet_version_id };
}

export function armarPayloadCargaAnular(b: BorradorAnularCarga): Record<string, unknown> {
	const carga = exigir(b.carga, 'la carga');
	return { envasado_id: carga.envasado_id, anulado_por: exigir(b.personas[0], 'quién anula'), motivo: exigir(motivoDeAnularCarga(b), 'el motivo'), pallet_version_id: b.pallet_version_id };
}

/** Las letras de una muestra, en el orden en que se ofrecen. */
export const LETRAS_MUESTRA = ['Q', 'P', 'L', 'descarte'] as const;

/**
 * Las versiones de muestra de este teléfono que el catálogo no refleja (v6, §12.5 y §12.9), por la
 * versión que reemplazan: la cadena se sigue hasta la última. Un cambio de letra trae su letra. Una
 * humedad (un teléfono de calidad que gestión pasó a envasado) cuenta solo si el servidor ya la
 * aplicó y el catálogo todavía no la refleja: es la versión vigente, y un cambio de letra contra la
 * anterior volvía como `no_vigente`. Su letra la decidió el servidor, así que no la trae: vale la del
 * último cambio de la cadena, o `null` y manda el catálogo. Una humedad todavía en la cola no cuenta:
 * el servidor la va a rechazar (`estacion_no_autorizada`, la humedad es de calidad) y la base sigue
 * siendo la anterior; colgar de ella haría caer al cambio de letra con su rechazo (5/10).
 */
export function cambiosDeLetra(
	mutaciones: Array<{ tipo: string; payload: unknown; mutacion_id?: string; cadena?: { bases: string[]; letra: string | null }; resumen?: unknown }>,
): Map<string, { muestra_id: string; letra: string | null }> {
	const porBase = new Map<string, { muestra_id: string; letra: string | null }>();
	for (const m of mutaciones) {
		// §12.17: una corrección de la humedad se trata como completarla (solo aplicada), pero su letra es conocida:
		// viajó con `regla_version`, así que la del resumen es la que escribió el servidor (Codex, ronda del plan, h. 7).
		// §12.18: la hoja corregida desde calidad, igual, con su letra en el payload.
		const esDeCalidad = m.tipo === 'muestra.humedad' || m.tipo === 'muestra.corregir_humedad' || m.tipo === 'muestra.corregir';
		if (m.tipo !== 'muestra.letra' && !(esDeCalidad && m.mutacion_id === undefined)) continue;
		const p = m.payload as { muestra_id?: unknown; corrige_a?: unknown; letra_asignada?: unknown };
		if (typeof p?.muestra_id !== 'string' || typeof p.corrige_a !== 'string') continue;
		if (m.tipo === 'muestra.letra' && typeof p.letra_asignada !== 'string') continue;
		const delResumen = m.tipo === 'muestra.corregir_humedad' ? (m.resumen as { entidad?: unknown; letra?: unknown } | undefined) : undefined;
		const letraConocida = delResumen?.entidad === 'muestra' && typeof delResumen.letra === 'string' ? delResumen.letra : null;
		const letraDelPayload = (m.tipo === 'muestra.letra' || m.tipo === 'muestra.corregir') && typeof p.letra_asignada === 'string' ? p.letra_asignada : null;
		const destino = { muestra_id: p.muestra_id, letra: letraDelPayload ?? letraConocida ?? m.cadena?.letra ?? null };
		porBase.set(p.corrige_a, destino);
		// Un recibo que pisó a los de versiones anteriores de la misma muestra: el catálogo puede seguir
		// mostrando cualquiera de ellas, y todas llevan a esta (ronda de disensor, 5/10).
		for (const base of m.cadena?.bases ?? []) if (!porBase.has(base)) porBase.set(base, destino);
	}
	// Una base que se cambió dos veces: la primera apunta a la última, con la última letra conocida.
	const ultima = (base: string): { muestra_id: string; letra: string | null } => {
		let actual = porBase.get(base)!;
		let letra = actual.letra;
		const vistos = new Set<string>();
		while (porBase.has(actual.muestra_id) && !vistos.has(actual.muestra_id)) {
			vistos.add(actual.muestra_id);
			actual = porBase.get(actual.muestra_id)!;
			letra = actual.letra ?? letra;
		}
		return { muestra_id: actual.muestra_id, letra };
	};
	return new Map([...porBase.keys()].map((base) => [base, ultima(base)]));
}

/** A qué letra de pallet va un precinto según su muestra: la suya, o D si dio descarte (§5.7 r. 17d). */
export function letraDestinoDe(letraDeLaMuestra: string | null): LetraPallet | null {
	if (letraDeLaMuestra === null) return null;
	if (letraDeLaMuestra === 'descarte') return 'D';
	return (LETRAS_PALLET as readonly string[]).includes(letraDeLaMuestra) ? (letraDeLaMuestra as LetraPallet) : null;
}

function exigir<T>(valor: T | null | undefined, campo: string): T {
	if (valor === null || valor === undefined) throw new BorradorIncompleto(`falta ${campo}`);
	return valor;
}

/** «8,25 kg», como se dice en la planta. */
export function formatoTexto(formatoG: number): string {
	return `${(formatoG / 1000).toFixed(2).replace('.', ',')} kg`;
}

/** Kilos con hasta dos decimales y coma («99», «24,75»), sin depender de qué locales trae el WebView. */
export function kgConDecimales(gramos: number): string {
	return String(Math.round(gramos / 10) / 100).replace('.', ',');
}

export function gramosDeCarga(bolsas: number, formatoG: number): number {
	return bolsas * formatoG;
}

/** Lo que queda del precinto: el neto del silo menos lo ya envasado; null sin pesada. */
export function saldoG(p: Pick<PrecintoVisto, 'neto_g' | 'envasado_g'>): number | null {
	return p.neto_g === null ? null : p.neto_g - p.envasado_g;
}

/** Aviso, no bloqueo: la carga pasa del saldo por más de una bolsa. */
export function sobrepasaElSilo(p: Pick<PrecintoVisto, 'neto_g' | 'envasado_g'>, bolsas: number | null, formatoG: number | null): boolean {
	const saldo = saldoG(p);
	return saldo !== null && bolsas !== null && formatoG !== null && gramosDeCarga(bolsas, formatoG) > saldo + formatoG;
}

/**
 * La tolerancia sobre el neto del silo: no se envasan más kilos que los producidos en ese silo, con
 * un margen. El 15/9 el margen era el 10 % (`[DICHO 15/9]` Agata, «quedan remanentes en la línea,
 * pero no más que eso»; `[DECIDIDO 15/9]` Nicolás). Desde el 7/10/2026 son **tres bolsas del
 * formato**, y la misma tolerancia define «embolsado entero» (`[DICHO 7/10]` Agata, «el margen
 * debería ser más-menos 30 kilos… 3 bolsas que pueden quedar en una especie de limbo entre dos
 * precintos… los kilos tarde o temprano van apareciendo… solo cambia el margen de permiso»;
 * `[DECIDIDO 7/10/2026]` Nicolás, opción A del issue #85; `docs/esquema.md` §12.14): el 10 % queda
 * solo para el silo grande en el que es más que las tres bolsas. En bolsas y no en kilos, para
 * que valga con 7,25 y con 8,25. Las mismas constantes en el servidor (`precinto_sobrepasado`,
 * §5.7 r. 18) y en el tablero (`ENTERO`); por debajo del tope, el aviso de `sobrepasaElSilo` sigue.
 */
export const BOLSAS_DE_TOLERANCIA = 3;
export const TOLERANCIA_SOBRE_NETO = 0.1;

/**
 * Bloqueo: con esta carga, lo envasado del precinto pasaría el neto del silo más la tolerancia:
 * tres bolsas del formato de la carga o el 10 %, lo que sea mayor. En enteros, como el servidor; y
 * como el servidor, sin pesada no decide (Codex, 15/9: el catálogo traía cero y una sola bolsa
 * bloqueaba lo que el servidor aceptaba).
 */
export function superaLaTolerancia(p: Pick<PrecintoVisto, 'neto_g' | 'envasado_g'>, bolsas: number | null, formatoG: number | null): boolean {
	if (p.neto_g === null || bolsas === null || formatoG === null) return false;
	const total = p.envasado_g + gramosDeCarga(bolsas, formatoG);
	return total > p.neto_g + BOLSAS_DE_TOLERANCIA * formatoG && total * 100 > p.neto_g * Math.round((1 + TOLERANCIA_SOBRE_NETO) * 100);
}

/**
 * Se embolsó entero: le quedan menos de tres bolsas del formato con que se embolsó (Agata, 15/9: «que
 * desaparezca»; 7/10: el margen son las tres bolsas del limbo, antes era una). Sin pesada no se sabe.
 */
export function precintoEntero(p: Pick<PrecintoVisto, 'neto_g' | 'envasado_g' | 'formato_previo'>): boolean {
	const saldo = saldoG(p);
	return saldo !== null && p.envasado_g > 0 && saldo < BOLSAS_DE_TOLERANCIA * (p.formato_previo ?? FORMATO_PREMARCADO);
}

/**
 * Embolsado entero, pero todavía le entra una bolsa dentro de la tolerancia: el remanente de la
 * línea que Agata contempla (15/9). Desaparecer al agotar el neto y a la vez tolerar el tope se
 * contradecían en una carga partida —12 bolsas y después la 13.ª— (Codex, ronda del diff de la
 * tolerancia, 15/9): estos se ofrecen aparte, bajo el mismo tope.
 */
export function admiteRemanente(p: Pick<PrecintoVisto, 'neto_g' | 'envasado_g' | 'formato_previo'>): boolean {
	return precintoEntero(p) && !superaLaTolerancia(p, 1, p.formato_previo ?? FORMATO_PREMARCADO);
}

export function bolsasValidas(bolsas: number | null): boolean {
	return bolsas !== null && Number.isInteger(bolsas) && bolsas >= 1 && bolsas <= MAX_BOLSAS_POR_CARGA;
}

/** Cuántos días lleva un precinto con letra sin embolsarse, contados por fecha de cierre. */
export function diasDesde(cerradoEn: string, ahoraMs: number): number {
	// Por fecha calendario en la hora de la planta, como los cuenta el tablero: un silo cerrado el 10 a
	// las 23:00 lleva 5 días el 15 a las 00:05, en los dos lados (Codex, ronda de la v5 entera, 15/9).
	const cerrado = Date.parse(`${cerradoEn.slice(0, 10)}T00:00:00Z`);
	const hoy = Date.parse(`${fechaLocal(ahoraMs)}T00:00:00Z`);
	return Number.isNaN(cerrado) || Number.isNaN(hoy) ? 0 : Math.max(0, Math.round((hoy - cerrado) / 86_400_000));
}

/** La partida del rótulo: campaña + letra + gramos por bolsa, sin corte (r. 5/9). */
export function partida(campaniaLetra: string, letra: string, formatoG: number): string {
	return `${campaniaLetra}·${letra}·${formatoG}`;
}

/**
 * El vencimiento: el primer envasado del pallet más un año (Nicolás, 14/9), sobre la fecha del
 * negocio (−03:00, como todo `tiempo.ts`) y no la del huso del teléfono. El 29 de febrero vence
 * el 28 del año siguiente.
 */
export function venceEn(primerEnvasadoMs: number): string {
	const [anio, mes, dia] = instanteLocal(primerEnvasadoMs).slice(0, 10).split('-').map(Number);
	return `${mes === 2 && dia === 29 ? 28 : dia}/${mes}/${anio + 1}`;
}

/**
 * Una fecha `AAAA-MM-DD` más un año, con la misma regla que `venceEn` (el 29 de febrero vence el
 * 28): es lo que gestión imprime como vencimiento del rótulo (r. 22) y lo que la ficha compara.
 */
export function masUnAnio(fecha: string): string {
	const [anio, mes, dia] = fecha.split('-').map(Number);
	const diaFinal = mes === 2 && dia === 29 ? 28 : dia;
	// El año a cuatro dígitos, como el GLOB del DDL y como `date()` de SQLite (Codex, 15/9).
	return `${String(anio + 1).padStart(4, '0')}-${String(mes).padStart(2, '0')}-${String(diaFinal).padStart(2, '0')}`;
}

/** «1/7/2027» a partir de `2027-07-01`, como lo dice el rótulo. */
export function fechaCorta(fecha: string): string {
	const [anio, mes, dia] = fecha.split('-').map(Number);
	return `${dia}/${mes}/${anio}`;
}

// ── Los rótulos disponibles (§5.7 r. 22) ──

/** Un rótulo emitido sin usar, tal como viene en `rotulos_disponibles` (§5.5). */
export interface RotuloDelCatalogo {
	campania_id: string;
	numero: number;
	/** v6 (§12.6): el correlativo de emisión; un catálogo de antes no lo trae y vale el número. */
	orden?: number;
	letra: string;
	formato_bolsa_g: number;
	envasado_desde: string;
}

/**
 * Los rótulos que se le pueden poner a un pallet de esa letra y formato: los emitidos sin usar del
 * catálogo, menos los que este teléfono ya usó y el catálogo no refleja —aperturas pendientes y
 * confirmadas sin reflejar, aunque su pallet ya esté cerrado (`palletsConocidos`)— y menos los
 * elegidos por OTROS borradores. El primero emitido (menor `orden`, §12.6) primero: es el que se propone.
 */
export function rotulosPara(
	catalogo: RotuloDelCatalogo[],
	letra: string,
	formatoBolsaG: number,
	usadosAca: Iterable<{ numero: number | null; campania_id?: string | null }>,
	elegidosPorOtros: Iterable<RotuloElegido>,
	/** Solo los de esta campaña, si se pide (al rehacer, la del pallet caído). */
	campaniaId: string | null = null,
): RotuloDelCatalogo[] {
	const tomado = rotuloTomado(usadosAca, elegidosPorOtros);
	return catalogo
		.filter((r) => r.letra === letra && r.formato_bolsa_g === formatoBolsaG && (campaniaId === null || r.campania_id === campaniaId) && !tomado(r.campania_id, r.numero))
		.sort((a, b) => (a.orden ?? a.numero) - (b.orden ?? b.numero) || a.numero - b.numero);
}

/**
 * ¿Ese rótulo ya está tomado en este teléfono? Por los pallets que pasaron por acá —por campaña y
 * número cuando la campaña se sabe; por número solo si no— y por lo elegido en otros borradores.
 */
export function rotuloTomado(usadosAca: Iterable<{ numero: number | null; campania_id?: string | null }>, elegidosPorOtros: Iterable<RotuloElegido>): (campaniaId: string, numero: number) => boolean {
	const usados = [...usadosAca].filter((p) => p.numero !== null);
	const elegidos = new Set<string>();
	for (const r of elegidosPorOtros) elegidos.add(`${r.campania_id}/${r.numero}`);
	return (campaniaId, numero) => usados.some((p) => p.numero === numero && (p.campania_id === undefined || p.campania_id === null || p.campania_id === campaniaId)) || elegidos.has(`${campaniaId}/${numero}`);
}

/** El número del rótulo con que viajó (o va a viajar) una apertura de la cola. */
export function numeroDeApertura(payload: unknown): number | null {
	return numero(payload, 'numero');
}

/**
 * Las mutaciones que vuelven a la cola al rehacer una apertura con otro rótulo (r. 22): la apertura
 * con el número nuevo, y sus hijos tal cual con ids nuevos y los padres reconstruidos —cada
 * `padre_id` viejo se traduce al id nuevo de la misma mutación; el orden es el de la cola—.
 */
export function mutacionesAlRehacer(
	apertura: MutacionCaida,
	hijos: MutacionCaida[],
	rotulo: RotuloElegido,
	idNuevo: () => string,
	/** Las mutaciones que siguen en la cola (pendientes o en la bandeja): un padre que no está ahí ni en el lote fue quitado por el encargado. */
	padresVigentes: Set<string> = new Set(),
): Array<MutacionCaida & { padre_id: string | null }> {
	const nuevos = new Map<string, string>([[apertura.id, idNuevo()]]);
	for (const h of hijos) nuevos.set(h.id, idNuevo());
	const aperturaNueva = nuevos.get(apertura.id) as string;
	// La apertura nueva de cada pallet del árbol: la caída y las que colgaban de ella (un segundo pallet
	// de la misma letra y formato).
	const aperturaDe = new Map<string, string>();
	const palletDeApertura = campo(apertura.payload, 'pallet_id');
	if (palletDeApertura !== null) aperturaDe.set(palletDeApertura, aperturaNueva);
	for (const h of hijos) {
		const palletId = campo(h.payload, 'pallet_id');
		if (h.tipo === 'pallet.abrir' && palletId !== null) aperturaDe.set(palletId, nuevos.get(h.id) as string);
	}
	// A qué pallet pertenece cada hijo, siguiendo la cadena de correcciones dentro del árbol.
	const palletDeCarga = new Map<string, string>();
	for (const h of hijos) {
		const envasadoId = campo(h.payload, 'envasado_id');
		if (envasadoId === null) continue;
		const pallet = h.tipo === 'envasado.crear' ? campo(h.payload, 'pallet_id') : (palletDeCarga.get(campo(h.payload, 'corrige_a') ?? '') ?? null);
		if (pallet !== null) palletDeCarga.set(envasadoId, pallet);
	}
	const palletDe = (h: MutacionCaida): string | null =>
		h.tipo.startsWith('pallet.') || h.tipo === 'envasado.crear' ? campo(h.payload, 'pallet_id') : (palletDeCarga.get(campo(h.payload, 'envasado_id') ?? campo(h.payload, 'corrige_a') ?? '') ?? null);
	// Un hijo cuyo padre fue quitado de la bandeja (una carga que el encargado descartó) cuelga de la
	// apertura nueva DE SU PALLET: viaja, y si su testigo ya no cierra, el servidor lo dice
	// (`composicion_cambio`) en vez de dejarlo huérfano para siempre (Codex, ronda del diff de 7b,
	// 15/9; el cierre de un segundo pallet colgaba del primero: ronda de la v5 entera, 15/9).
	const traducir = (h: MutacionCaida): string | null => {
		if (h.padre_id === null) return null;
		if (nuevos.has(h.padre_id)) return nuevos.get(h.padre_id) as string;
		if (padresVigentes.has(h.padre_id)) return h.padre_id;
		const propia = aperturaDe.get(palletDe(h) ?? '');
		// Una apertura huérfana no cuelga de sí misma: vuelve a la apertura caída, como antes.
		return propia === undefined || propia === nuevos.get(h.id) ? aperturaNueva : propia;
	};
	// La campaña es la del pallet caído, no la de ahora: el rótulo tiene que ser de esa (Codex, 15/9).
	const payloadApertura = { ...(apertura.payload as Record<string, unknown>), campania_id: rotulo.campania_id, numero: rotulo.numero };
	return [
		{ ...apertura, id: aperturaNueva, payload: payloadApertura, padre_id: apertura.padre_id === null ? null : (padresVigentes.has(apertura.padre_id) ? apertura.padre_id : null) },
		...hijos.map((h) => ({ ...h, id: nuevos.get(h.id) as string, padre_id: traducir(h) })),
	];
}

/** La campaña con que viajó una apertura de la cola. */
export function campaniaDeApertura(payload: unknown): string | null {
	return campo(payload, 'campania_id');
}

/** Los números de rótulo de las aperturas de una lista de mutaciones caídas (las que un rehacer va a reencolar). */
export function numerosDeAperturas(mutaciones: Array<{ tipo: string; payload: unknown }>): number[] {
	return mutaciones.filter((m) => m.tipo === 'pallet.abrir').map((m) => numeroDeApertura(m.payload)).filter((n): n is number => n !== null);
}

// ── Payloads (§5.7) ──

export function armarPayloadPalletAbrir(
	p: {
		pallet_id: string;
		version_id: string;
		letra: string | null;
		formato_bolsa_g: number | null;
		personas_pallet: string[];
		abierto_ms: number | null;
		nota: string | null;
		persona: string | undefined;
		rotulo: RotuloElegido | null;
		/** Una tarima de espera: sin letra ni rótulo; la campaña es la del precinto que recibe. */
		tarima?: { campania_id: string | null } | null;
	},
): Record<string, unknown> {
	if (p.personas_pallet.length < 1 || p.personas_pallet.length > MAX_PERSONAS_PALLET) throw new BorradorIncompleto('quiénes embolsan');
	const comun = {
		pallet_id: p.pallet_id,
		version_id: p.version_id,
		formato_bolsa_g: exigir(p.formato_bolsa_g, 'formato'),
		abierto_en: instanteLocal(exigir(p.abierto_ms, 'hora de apertura')),
		personas: [...p.personas_pallet],
		persona_id: exigir(p.persona, 'quién'),
		notas: p.nota?.trim() ? p.nota.trim() : null,
	};
	if (p.tarima) {
		return { ...comun, campania_id: exigir(p.tarima.campania_id, 'campaña'), es_tarima: 1, numero: null, letra: null };
	}
	// El rótulo trae la campaña (r. 22): es la que la embolsadora vio al elegirlo.
	const rotulo = exigir(p.rotulo, 'rótulo');
	return { ...comun, campania_id: rotulo.campania_id, es_tarima: 0, numero: rotulo.numero, letra: exigir(p.letra, 'letra') };
}

/**
 * Con el precinto en una tarima de espera (§12.21): cuántas de las bolsas tipeadas salen de la tarima, cuántas son nuevas
 * del silo y cuántas quedan en espera. Null sin tarima o sin bolsas.
 */
export function repartoDeBolsas(b: Pick<BorradorEmbolsar, 'bolsas' | 'precinto'>): { de_la_tarima: number; nuevas: number; quedan: number } | null {
	const t = b.precinto?.en_tarima ?? null;
	if (t === null || b.bolsas === null) return null;
	const deLaTarima = Math.min(b.bolsas, t.bolsas);
	return { de_la_tarima: deLaTarima, nuevas: b.bolsas - deLaTarima, quedan: t.bolsas - deLaTarima };
}

/** Las bolsas que cuentan contra el tope del precinto: las nuevas del silo (las de la tarima ya están en `envasado_g`). */
export function bolsasNuevas(b: Pick<BorradorEmbolsar, 'bolsas' | 'precinto'>): number | null {
	if (b.bolsas === null) return null;
	return repartoDeBolsas(b)?.nuevas ?? b.bolsas;
}

/**
 * `pallet.unir` (r. 17d): la tarima pasa al destino; `cerrado_en` es la unión. Con menos bolsas que las de la tarima es
 * de a partes (§12.21): viajan las que pasan, el testigo de las vistas, la versión nueva de la carga y la parte que nace
 * unida; con todas (o más: el resto va en una carga aparte), la unión entera de siempre.
 */
export function armarPayloadPalletUnir(b: BorradorEmbolsar, destinoPalletId: string): Record<string, unknown> {
	const tarima = exigir(b.precinto?.en_tarima, 'tarima');
	const reparto = exigir(b.reparto, 'reparto');
	const bolsas = exigir(b.bolsas, 'bolsas');
	const comun = {
		pallet_id: tarima.pallet_id,
		version_id: reparto.version_id,
		version_base_id: tarima.version_id,
		destino_pallet_id: destinoPalletId,
		cerrado_en: instanteLocal(exigir(b.registrado_ms, 'hora')),
		persona_id: exigir(b.personas[0], 'quién'),
	};
	if (bolsas >= tarima.bolsas) return comun;
	return { ...comun, bolsas, bolsas_vistas: tarima.bolsas, carga_version_id: reparto.carga_version_id, parte: { ...reparto.parte } };
}

/** `pallet.anular` (r. 17c): con motivo; `cerrado_en` es cuando se tiró, porque todavía recibía. */
export function armarPayloadPalletAnular(b: BorradorAnularPallet): Record<string, unknown> {
	const pallet = exigir(b.pallet, 'pallet');
	return {
		pallet_id: pallet.pallet_id,
		version_id: b.version_id,
		version_base_id: pallet.version_id,
		cerrado_en: instanteLocal(exigir(b.cerrado_ms, 'hora')),
		motivo_anulacion_pallet: exigir(b.motivo, 'motivo'),
		persona_id: exigir(b.personas[0], 'quién'),
	};
}

export function armarPayloadEnvasadoCrear(b: BorradorEmbolsar, palletId: string, bolsas: number | null = b.bolsas): Record<string, unknown> {
	if (!bolsasValidas(bolsas)) throw new BorradorIncompleto('bolsas fuera de rango');
	return {
		envasado_id: b.envasado_id,
		llenado_id: exigir(b.precinto, 'precinto').llenado_id,
		pallet_id: palletId,
		cant_bolsas: bolsas,
		registrado_en: instanteLocal(exigir(b.registrado_ms, 'hora')),
		persona_id: exigir(b.personas[0], 'quién'),
		notas: b.nota?.trim() ? b.nota.trim() : null,
	};
}

export function armarPayloadPalletCerrar(b: BorradorCerrarPallet): Record<string, unknown> {
	const pallet = exigir(b.pallet, 'pallet');
	if (pallet.bolsas < 1) throw new BorradorIncompleto('un pallet vacío no se cierra');
	return {
		pallet_id: pallet.pallet_id,
		version_id: b.version_id,
		version_base_id: pallet.version_id,
		cerrado_en: instanteLocal(exigir(b.cerrado_ms, 'hora de cierre')),
		// El testigo de composición: lo que este teléfono vio (r. 17b).
		bolsas: pallet.bolsas,
		persona_id: exigir(b.personas[0], 'quién'),
	};
}

/** Cerrar, unir o tirar no pueden quedar antes de la apertura ni de la última carga directa del pallet (`cierre_anterior_a_apertura`, `cierre_antes_de_la_carga`). */
export function cierreDePalletValido(cerradoMs: number | null, pallet: Pick<PalletVisto, 'abierto_en' | 'ultima_carga_en'> | null): boolean {
	if (cerradoMs === null || pallet === null) return false;
	const cierre = instanteLocal(cerradoMs);
	return cierre > pallet.abierto_en && (pallet.ultima_carga_en === null || cierre >= pallet.ultima_carga_en);
}

/** Una carga no puede quedar antes de la apertura del pallet que la recibe (`carga_antes_del_pallet`); sin destino elegido no hay con qué comparar. */
export function cargaDentroDelPallet(registradoMs: number | null, destino: Pick<PalletVisto, 'abierto_en'> | null): boolean {
	if (registradoMs === null) return false;
	return destino === null || instanteLocal(registradoMs) >= destino.abierto_en;
}

/** Cuántos precintos distintos lleva un pallet, con los de sus tarimas unidas: un precinto partido en dos cargas cuenta una vez. */
export function precintosDe(p: Pick<PalletAbiertoServidor, 'envasados' | 'tarimas'>): number {
	return new Set([...p.envasados, ...p.tarimas.flatMap((t) => t.envasados)].map((e) => e.llenado_id)).size;
}

/**
 * A qué pallet en armado iría un precinto, antes de las bolsas (Agata, 15/10/2026: «ese es el dato que les falta…
 * para que puedan saber a qué palet ir armando»; §12.18). La regla es la del paso del pallet: uno en armado por letra
 * y formato, y un precinto que ya se embolsó en un formato solo va a ese. Sin formato fijado, el de cada formato
 * que haya (Agata: con dos de la misma letra, «sí, está bien» que se vean los dos), el premarcado primero. Vacío es
 * «pallet nuevo»; null, un precinto sin letra (va a una tarima, que no se anticipa).
 */
export function destinosDelPrecinto<P extends Pick<PalletAbiertoServidor, 'es_tarima' | 'letra' | 'formato_bolsa_g'>>(
	letra: string | null,
	formatoPrevio: number | null,
	pallets: readonly P[],
): P[] | null {
	const destino = letraDestinoDe(letra);
	if (destino === null) return null;
	return FORMATOS_BOLSA.filter((f) => formatoPrevio === null || f === formatoPrevio).flatMap((f) =>
		pallets.filter((p) => p.es_tarima === 0 && p.letra === destino && p.formato_bolsa_g === f).slice(0, 1),
	);
}

/** Una carga del pallet tal como se ve debajo de su precinto: cuántas bolsas, quién, cuándo y, si vino de una tarima unida, de cuál. */
export interface CargaDelPallet {
	envasado_id: string;
	cant_bolsas: number;
	persona_id: string;
	registrado_en: string;
	local: boolean;
	tarima: TarimaUnidaServidor | null;
}

/** Un precinto del pallet con el total de sus cargas. */
export interface PrecintoDelPallet {
	llenado_id: string;
	precinto: string | null;
	bolsas: number;
	cargas: CargaDelPallet[];
}

/**
 * El pallet por precinto (Agata, 15/10/2026: «¿cuántas le asignan a cada precinto para conformar el palet?»;
 * §12.18): cada precinto con el total de sus bolsas, directas o de una tarima unida, y debajo cada carga con quién y
 * cuándo. Los precintos van en el orden de su primera carga y las cargas, por hora.
 */
export function cargasPorPrecinto(p: Pick<PalletAbiertoServidor, 'envasados' | 'tarimas'>): PrecintoDelPallet[] {
	const cargas = [
		...p.envasados.map((e) => ({ e, tarima: null as TarimaUnidaServidor | null })),
		...p.tarimas.flatMap((t) => t.envasados.map((e) => ({ e, tarima: t as TarimaUnidaServidor | null }))),
	].sort((a, b) => a.e.registrado_en.localeCompare(b.e.registrado_en));
	const precintos = new Map<string, PrecintoDelPallet>();
	for (const { e, tarima } of cargas) {
		const grupo = precintos.get(e.llenado_id) ?? { llenado_id: e.llenado_id, precinto: null, bolsas: 0, cargas: [] };
		grupo.precinto ??= e.precinto;
		grupo.bolsas += e.cant_bolsas;
		// «Sin subir»: una carga o una corrección (§12.18) que todavía espera en la cola.
		grupo.cargas.push({ envasado_id: e.envasado_id, cant_bolsas: e.cant_bolsas, persona_id: e.persona_id, registrado_en: e.registrado_en, local: e.local === true || e.mutacion_id !== undefined, tarima });
		precintos.set(e.llenado_id, grupo);
	}
	return [...precintos.values()];
}
